import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import type { ExperienceAttributes } from './ExperienceEvent';
import { collectAll } from './ExperienceEventCollector';
import {
	SELECT_INLINE_COMMENT_EXPERIENCE,
	SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
	SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
} from './ExperienceName';
import { getExperienceTracker } from './getExperienceTracker';

let runningExperiences: string[] = [];
let commonSelectInlineCommentExperiencesAttributes: ExperienceAttributes = {};

function getSelectInlineCommentExperienceDebug(experienceName: string) {
	function createInlineCommentExperienceDebug({
		error,
		createAnalyticsEvent,
		extraAttributes,
	}: {
		createAnalyticsEvent: CreateUIAnalyticsEvent;
		error: Error;
		extraAttributes?: ExperienceAttributes;
	}) {
		const experienceTracker = getExperienceTracker();

		const experienceAttributes = experienceTracker.getExperienceState(
			SELECT_INLINE_COMMENT_EXPERIENCE,
		);
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'error',
				actionSubject: SELECT_INLINE_COMMENT_EXPERIENCE,
				source: experienceName,
				attributes: {
					...experienceAttributes,
					...commonSelectInlineCommentExperiencesAttributes,
					...extraAttributes,

					error: error.message || String(error),
					stack: error.stack,
				},
			},
		}).fire();
	}
	return createInlineCommentExperienceDebug;
}

type EntryPoint =
	| 'create-comment'
	| 'annotation-change'
	| 'comment-navigation'
	| 'keyboard-navigation'
	| 'sidebar'
	| 'url-query'
	| 'unknown:RendererFocus'
	| 'unknown:InlineComment'
	| 'unknown:CommentsPanelList';

type StartAttributes =
	| {
			pageClass: 'editor' | 'renderer';
			/**
			 * This is optional as in some scenarios the entry point does not know
			 * what type of comment is being selected (e.g. comment navigation, sidebar).
			 **/
			commentType?: 'block' | 'inline';
			blockType?: 'media';
			annotationId: string;
			entryPoint: Exclude<EntryPoint, 'keyboard-navigation' | 'comment-navigation'>;
	  }
	| {
			pageClass: 'editor' | 'renderer';
			/**
			 * This is optional as in some scenarios the entry point does not know
			 * what type of comment is being selected (e.g. comment navigation, sidebar).
			 **/
			commentType?: 'block' | 'inline';
			blockType?: 'media';
			entryPoint: 'keyboard-navigation' | 'comment-navigation';
	  };

/**
 * Comment selection can occur in a variety of ways;
 * - with sidebar open
 *   - select a document annotation
 *   - select a comment in the sidebar
 *   - select a subsequent comment in the sidebar (different logic is used between first and subsequent sidebar selections)
 * - with sidebar closed
 *   - select a document annotation
 *   - using the keyboard or ui next/previous navigations
 *   - via loading a page with a comment in the query string
 * - via notifications
 *
 * The experience tracker has a number of constraints;
 *
 * At the start of the experience, the “show” and “select” can be in different orders
 *  - sidebar vs panel vs annotation selection entrypoints
 * To accommodate this – the parent experience does not have an explicit start, and will be started when either the select or show sub experiences start.
 * To avoid restarts – when starting the parent experience – we check if there is an experience running with the same annotation id, and if so do nothing (ie. don’t start a new one).
 *
 * At the start of the experience, the annotationId is not always known
 * - panel next/previous navigation
 * When a start occurs in this scenario, we always restart the experience
 *
 * For some experience the first time we get a reliable signal that an annotation has been selected – is when the selection has completed
 * - annotation based selections and un wired product selections (at time of writing, notifications)
 * To accommodate this – on selection complete – we check if there is an active select experience, and if not start one.
 */
export const selectInlineCommentCompoundExperience = {
	/**
	 * Intended for internal use only.
	 *
	 * When the selectAnnotation and selectAnnotation are started they
	 * fire this with the start properties.
	 */
	_start: (startAttributes: StartAttributes) => {
		const experienceTracker = getExperienceTracker();
		const runningExperienceAttributes = experienceTracker.getExperienceState(
			SELECT_INLINE_COMMENT_EXPERIENCE,
		)?.attributes;

		// for keyboard navigation, we don't know the annotationId when the experience starts
		if ('annotationId' in startAttributes) {
			if (startAttributes.annotationId === runningExperienceAttributes?.annotationId) {
				// If there is an existing experience tracker for the same annotationId
				// see we assume the experience is still active, and don't start a new one.
				// However -- if the second start has a more specific entry point
				// we update the entry point attribute of the existing experience.
				if (
					(runningExperienceAttributes.entryPoint as string)?.startsWith('unknown') &&
					!startAttributes.entryPoint.startsWith('unknown')
				) {
					selectInlineCommentCompoundExperience.addCommonAttributes({
						entryPoint: startAttributes.entryPoint,
					});
				}

				return;
			}
		}

		// We reset experience state if we are starting a new experience
		runningExperiences = [SELECT_INLINE_COMMENT_EXPERIENCE];
		commonSelectInlineCommentExperiencesAttributes = {
			pageClass: startAttributes.pageClass,
			commentType: startAttributes.commentType,
			annotationId: 'annotationId' in startAttributes ? startAttributes.annotationId : undefined,
			entryPoint: startAttributes.entryPoint,
		};

		// THen start a new compound experience
		experienceTracker.start({
			name: SELECT_INLINE_COMMENT_EXPERIENCE,
			attributes: commonSelectInlineCommentExperiencesAttributes,
			// If the annotation is not selected and shown to the user within 7 seconds -- we assume there has been a failure to select the annotation
			timeout: 7000,

			collect: (events, experience) => {
				collectAll([
					SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
					SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
				])(events, experience);
			},
		});

		const compoundTaskId = experienceTracker.getExperienceState(
			SELECT_INLINE_COMMENT_EXPERIENCE,
		)?.taskId;
		if (compoundTaskId) {
			// this is expected to always be the case, the condition is to keep typescript
			// happy.
			selectInlineCommentCompoundExperience.addCommonAttributes({ compoundTaskId });
		}
	},
	addCommonAttributes: (additionalCommonAttributes: {
		[key: string]: string | number | boolean;
	}) => {
		const experienceTracker = getExperienceTracker();
		commonSelectInlineCommentExperiencesAttributes = {
			...commonSelectInlineCommentExperiencesAttributes,
			...additionalCommonAttributes,
		};
		for (const runningExperience of runningExperiences) {
			experienceTracker.updateAttributes({
				name: runningExperience,
				attributes: additionalCommonAttributes,
			});
		}
	},
	abort: ({ abortReason }: { abortReason: 'test abort' }) => {
		const experienceTracker = getExperienceTracker();
		experienceTracker.abort({
			name: SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
			reason: abortReason,
		});
	},
	selectAnnotation: {
		debug: getSelectInlineCommentExperienceDebug(
			SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
		),
		start: (startAttributes: StartAttributes) => {
			const experienceTracker = getExperienceTracker();
			selectInlineCommentCompoundExperience._start(startAttributes);
			const experienceState = experienceTracker.getExperienceState(
				SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
			);
			if (
				'annotationId' in startAttributes &&
				experienceState !== null &&
				experienceState.attributes?.annotationId === startAttributes.annotationId
			) {
				// If the experience has already started -- we don't start a new one.
				// while this is not hit when testing in a single tab.  Race conditions can occur when
				// the user navigates to another tab after pressing save and then returning to the tab.
				return;
			}
			experienceTracker.start({
				name: SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
				attributes: commonSelectInlineCommentExperiencesAttributes,
				timeout: 5000,
			});
			runningExperiences.push(SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE);
		},
		abort: ({ abortReason }: { abortReason: 'Comment navigation when only one comment' }) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.abort({
				name: SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
				reason: abortReason,
			});
		},
		complete: (startAttributes: StartAttributes) => {
			const experienceTracker = getExperienceTracker();
			// There can be cases where no start has been called for the select experience.
			// - When a product consumer of the editor setselectedannotation api calls the select experience
			//   and has not been instrumented.
			// - When the annotation selection is via editor logic (ie. editor selection being moved onto
			//   an annotation).
			const experienceState = experienceTracker.getExperienceState(
				SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
			);
			if (
				experienceState === null ||
				// When the experience state has stopped -- we assume it's from a previous experience
				experienceState.hasStopped === true
			) {
				selectInlineCommentCompoundExperience.selectAnnotation.start(startAttributes);
			}
			experienceTracker.succeed({
				name: SELECT_INLINE_COMMENT_SELECT_ANNOTATION_EXPERIENCE,
				attributes: commonSelectInlineCommentExperiencesAttributes,
			});
		},
	},
	showComment: {
		debug: getSelectInlineCommentExperienceDebug(SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE),
		start: (startAttributes: StartAttributes) => {
			const experienceTracker = getExperienceTracker();
			selectInlineCommentCompoundExperience._start(startAttributes);
			experienceTracker.start({
				name: SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
				attributes: commonSelectInlineCommentExperiencesAttributes,
				timeout: 5000,
			});
			runningExperiences.push(SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE);
		},
		fail: (error: Error, extraAttributes?: { misalignedBy: number }) => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.fail({
				name: SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
				error,
				attributes: { ...commonSelectInlineCommentExperiencesAttributes, ...extraAttributes },
			});
		},
		complete: () => {
			const experienceTracker = getExperienceTracker();
			experienceTracker.succeed({
				name: SELECT_INLINE_COMMENT_SHOW_COMMENT_EXPERIENCE,
				attributes: commonSelectInlineCommentExperiencesAttributes,
			});
		},
	},
};
